import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/client';
import type { ProjectSettings, UserSummary } from '../../api/types';
import { Alert } from '../../ui/feedback';
import { Checkbox, Field, FormSection, Input, ListEditor, Select, Textarea } from '../../ui/forms';
import type { Binding } from './types';

export interface CandidateValues {
  title: string;
  environmentId: string;
  ticketKeys: string[];
  sourceRevision: string;
  artifactDigest: string;
  artifactRef: string;
  integrationEvidence: string;
  deploymentPlan: string;
  verificationChecks: string[];
  rollbackPlan: string;
  executor: { kind: 'human'; userId: string } | { kind: 'ai_service'; serviceId: string };
}

interface Releasable {
  key: string;
  number: number;
  title: string;
  reservedBy: string | null;
  unresolvedDescendants: string[];
}

export const fromBinding = (b: Binding): CandidateValues => ({
  title: b.title,
  environmentId: b.environmentId,
  ticketKeys: b.tickets?.map((t) => t.key) ?? [],
  sourceRevision: b.sourceRevision ?? '',
  artifactDigest: b.artifactDigest ?? '',
  artifactRef: b.artifactRef ?? '',
  integrationEvidence: b.integrationEvidence ?? '',
  deploymentPlan: b.deploymentPlan ?? '',
  verificationChecks: b.verificationChecks,
  rollbackPlan: b.rollbackPlan ?? '',
  executor:
    b.executor.kind === 'human'
      ? { kind: 'human', userId: b.executor.userId }
      : { kind: 'ai_service', serviceId: b.executor.serviceId },
});

export const toPayload = (v: CandidateValues) => ({
  ...v,
  artifactDigest: v.artifactDigest || null,
  artifactRef: v.artifactRef || null,
  verificationChecks: v.verificationChecks.filter((c) => c.trim()),
});

/** WF-10 binding editor: ready tickets with family checks, exact revision/artifact, environment, executor, plans. */
export function CandidateForm({
  projectKey,
  value,
  onChange,
  currentTickets = [],
}: {
  projectKey: string;
  value: CandidateValues;
  onChange: (v: CandidateValues) => void;
  currentTickets?: string[];
}) {
  const settings = useQuery({
    queryKey: ['settings', projectKey],
    queryFn: () => api.get<ProjectSettings>(`/projects/${projectKey}/settings`),
  });
  const releasable = useQuery({
    queryKey: ['releasable', projectKey],
    queryFn: () => api.get<Releasable[]>(`/projects/${projectKey}/releases/releasable`),
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserSummary[]>('/admin/users') });
  const services = useQuery({
    queryKey: ['ai-services'],
    queryFn: () => api.get<{ id: string; name: string; active: boolean }[]>('/admin/ai-services'),
  });
  const [execKind, setExecKind] = useState(value.executor.kind);
  const set = (patch: Partial<CandidateValues>) => onChange({ ...value, ...patch });
  const env = settings.data?.environments.find((e) => e.id === value.environmentId);
  const selected = new Set(value.ticketKeys);
  const missingFamily = (releasable.data ?? [])
    .filter((t) => selected.has(t.key))
    .flatMap((t) =>
      t.unresolvedDescendants.filter((d) => !selected.has(d)).map((d) => `${t.key} needs ${d}`),
    );
  return (
    <>
      <FormSection title="Release">
        <Field label="Title">
          {(p) => (
            <Input {...p} value={value.title} onChange={(e) => set({ title: e.target.value })} required />
          )}
        </Field>
        <Field
          label="Target environment"
          hint={
            env
              ? env.isCompletionTarget
                ? 'Completion target: a successful attempt closes the included tickets.'
                : 'Not the completion target: tickets stay Pending release after success.'
              : undefined
          }
        >
          {(p) => (
            <Select
              {...p}
              value={value.environmentId}
              onChange={(e) => set({ environmentId: e.target.value })}
              required
            >
              <option value="">Choose an environment…</option>
              {(settings.data?.environments ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                  {e.isCompletionTarget ? ' (completion target)' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </FormSection>
      <FormSection
        title="Tickets"
        description="Only tickets in Pending release with a current readiness snapshot. Unresolved children must be included."
      >
        {(releasable.data ?? []).length === 0 && (
          <span className="small muted">No tickets are in Pending release.</span>
        )}
        <div className="stack sm">
          {(releasable.data ?? []).map((t) => {
            const reservedElsewhere = !!t.reservedBy && !currentTickets.includes(t.key);
            return (
              <Checkbox
                key={t.key}
                label={
                  <>
                    <span className="mono">{t.key}</span> {t.title}
                  </>
                }
                description={
                  reservedElsewhere
                    ? `Reserved by ${t.reservedBy}`
                    : t.unresolvedDescendants.length
                      ? `Includes children ${t.unresolvedDescendants.join(', ')}`
                      : undefined
                }
                disabled={reservedElsewhere}
                checked={selected.has(t.key)}
                onChange={(on) =>
                  set({
                    ticketKeys: on
                      ? [...value.ticketKeys, t.key]
                      : value.ticketKeys.filter((k) => k !== t.key),
                  })
                }
              />
            );
          })}
        </div>
        {missingFamily.length > 0 && (
          <Alert tone="warning" title="Family incomplete">
            {missingFamily.join('; ')}
          </Alert>
        )}
      </FormSection>
      <FormSection title="Binding" description="Mutable branch names or image tags alone are not a binding.">
        <Field label="Exact source revision (commit SHA)">
          {(p) => (
            <Input
              {...p}
              className="mono"
              value={value.sourceRevision}
              onChange={(e) => set({ sourceRevision: e.target.value })}
              required
            />
          )}
        </Field>
        <div className="grid-2">
          <Field label="Artifact digest" optional>
            {(p) => (
              <Input
                {...p}
                className="mono"
                value={value.artifactDigest}
                onChange={(e) => set({ artifactDigest: e.target.value })}
                placeholder="sha256:…"
              />
            )}
          </Field>
          <Field label="Artifact reference" optional>
            {(p) => (
              <Input
                {...p}
                className="mono"
                value={value.artifactRef}
                onChange={(e) => set({ artifactRef: e.target.value })}
                placeholder="registry/app@sha256:…"
              />
            )}
          </Field>
        </div>
        <Field
          label="Integration evidence"
          hint="How the final revision contains each included change; CI links."
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={value.integrationEvidence}
              onChange={(e) => set({ integrationEvidence: e.target.value })}
              required
            />
          )}
        </Field>
      </FormSection>
      <FormSection title="Plans">
        <Field label="Deployment plan">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={value.deploymentPlan}
              onChange={(e) => set({ deploymentPlan: e.target.value })}
              required
            />
          )}
        </Field>
        <div className="field">
          <span className="label">Required verification checks</span>
          <ListEditor
            label="Check"
            items={value.verificationChecks}
            onChange={(verificationChecks) => set({ verificationChecks })}
            addLabel="Add check"
            placeholder="e.g. smoke tests pass"
          />
        </div>
        <Field label="Rollback plan">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={value.rollbackPlan}
              onChange={(e) => set({ rollbackPlan: e.target.value })}
              required
            />
          )}
        </Field>
      </FormSection>
      <FormSection
        title="Executor"
        description="Only the designated executor can start and report the approved attempt."
      >
        <Select
          aria-label="Executor type"
          value={execKind}
          onChange={(e) => {
            const k = e.target.value as 'human' | 'ai_service';
            setExecKind(k);
            set({
              executor: k === 'human' ? { kind: 'human', userId: '' } : { kind: 'ai_service', serviceId: '' },
            });
          }}
        >
          <option value="human">Admin (manual execution)</option>
          <option value="ai_service">AI agent</option>
        </Select>
        {value.executor.kind === 'human' ? (
          <Field label="Admin">
            {(p) => (
              <Select
                {...p}
                value={value.executor.kind === 'human' ? value.executor.userId : ''}
                onChange={(e) => set({ executor: { kind: 'human', userId: e.target.value } })}
              >
                <option value="">Choose…</option>
                {(users.data ?? [])
                  .filter((u) => u.role === 'admin' && u.active)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field label="AI agent" hint="Needs delivery execution access to this project.">
            {(p) => (
              <Select
                {...p}
                value={value.executor.kind === 'ai_service' ? value.executor.serviceId : ''}
                onChange={(e) => set({ executor: { kind: 'ai_service', serviceId: e.target.value } })}
              >
                <option value="">Choose…</option>
                {(services.data ?? [])
                  .filter((s) => s.active)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        )}
      </FormSection>
    </>
  );
}
